import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import AdminIcon from './AdminIcon';

export default function FolderMenu({ name, disabled, onAdd, onRename, onDelete }: {
  name: string; disabled: boolean; onAdd: () => void; onRename: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null), panel = useRef<HTMLDivElement>(null);
  const close = (focus = false) => { setOpen(false); if (focus) trigger.current?.focus({ preventScroll: true }); };
  useLayoutEffect(() => {
    if (!open || !panel.current || !trigger.current) return;
    const rect = trigger.current.getBoundingClientRect(), menu = panel.current;
    menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - 208))}px`;
    menu.style.top = `${Math.max(8, rect.bottom + menu.offsetHeight + 8 > window.innerHeight ? rect.top - menu.offsetHeight - 4 : rect.bottom + 4)}px`;
    menu.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => { if (!panel.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) close(); };
    const scroll = (event: Event) => { if (!panel.current?.contains(event.target as Node)) close(); };
    document.addEventListener('mousedown', outside); document.addEventListener('touchstart', outside);
    window.addEventListener('scroll', scroll, true); window.addEventListener('resize', scroll);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('touchstart', outside); window.removeEventListener('scroll', scroll, true); window.removeEventListener('resize', scroll); };
  }, [open]);
  function keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    if (event.key === 'Tab') { close(true); return; }
    const items = Array.from(panel.current?.querySelectorAll<HTMLButtonElement>('button') ?? []);
    let index = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown') index = (index + 1) % items.length;
    else if (event.key === 'ArrowUp') index = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = items.length - 1;
    else return;
    event.preventDefault(); items[index]?.focus();
  }
  const run = (action: () => void) => { close(true); action(); };
  return <><button ref={trigger} type="button" className="folder-icon-button" disabled={disabled} aria-label={`文件夹操作：${name}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)} onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }}><AdminIcon name="more" /></button>
    {open && createPortal(<div ref={panel} className="admin-action-menu" role="menu" aria-label={`${name}的文件夹操作`} onKeyDown={keydown}>
      <button type="button" role="menuitem" tabIndex={-1} onClick={() => run(onAdd)}><AdminIcon name="plus" />添加子文件夹</button>
      <button type="button" role="menuitem" tabIndex={-1} onClick={() => run(onRename)}><AdminIcon name="edit" />重命名</button>
      <div className="admin-menu-divider" role="separator" />
      <button type="button" role="menuitem" tabIndex={-1} className="admin-menu-danger" onClick={() => run(onDelete)}><AdminIcon name="trash" />删除文件夹</button>
    </div>, document.body)}
  </>;
}
